import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

type TaxExemptionType = 'CUSTOMER' | 'PRODUCT' | 'CUSTOMER_TAX_RATE' | 'PRODUCT_TAX_RATE'

interface TaxExemption {
  id: string
  exemptionType: TaxExemptionType
  customerId?: string
  customer?: {
    id: string
    name: string
  }
  productName?: string
  taxRateId?: string
  taxRate?: {
    id: string
    name: string
    rate: string
  }
  isActive: boolean
  createdAt: string
}

interface Customer {
  id: string
  name: string
}

interface TaxRate {
  id: string
  name: string
  rate: string
}

const EXEMPTION_TYPES: Array<{ value: TaxExemptionType; label: string; description: string }> = [
  { value: 'CUSTOMER', label: 'Customer (All Taxes)', description: 'Exempt customer from all taxes' },
  { value: 'CUSTOMER_TAX_RATE', label: 'Customer (Specific Tax)', description: 'Exempt customer from a specific tax rate' },
  { value: 'PRODUCT', label: 'Product (All Taxes)', description: 'Exempt product from all taxes' },
  { value: 'PRODUCT_TAX_RATE', label: 'Product (Specific Tax)', description: 'Exempt product from a specific tax rate' },
]

const TaxExemptions: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [exemptions, setExemptions] = useState<TaxExemption[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedExemption, setSelectedExemption] = useState<TaxExemption | null>(null)
  const [formData, setFormData] = useState({
    exemptionType: 'CUSTOMER' as TaxExemptionType,
    customerId: '',
    productName: '',
    taxRateId: '',
    isActive: true,
  })
  const [filterType, setFilterType] = useState<string>('')

  useEffect(() => {
    fetchExemptions()
    fetchCustomers()
    fetchTaxRates()
  }, [])

  const fetchExemptions = async () => {
    try {
      setIsLoading(true)
      const params: any = {}
      if (filterType) {
        params.exemptionType = filterType
      }
      const response = await axios.get('/api/erp/tax-exemptions', { params })
      setExemptions(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching tax exemptions:', error)
      toast.error('Failed to load tax exemptions')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/clients')
      setCustomers(response.data.data || [])
    } catch (error) {
      console.error('Error fetching customers:', error)
    }
  }

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get('/api/erp/tax-rates', { params: { isActive: 'true' } })
      setTaxRates(response.data.data || [])
    } catch (error) {
      console.error('Error fetching tax rates:', error)
    }
  }

  useEffect(() => {
    fetchExemptions()
  }, [filterType])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const payload: any = {
        exemptionType: formData.exemptionType,
        isActive: formData.isActive,
      }

      if (formData.exemptionType === 'CUSTOMER' || formData.exemptionType === 'CUSTOMER_TAX_RATE') {
        if (!formData.customerId) {
          toast.error('Please select a customer')
          return
        }
        payload.customerId = formData.customerId
      }

      if (formData.exemptionType === 'PRODUCT' || formData.exemptionType === 'PRODUCT_TAX_RATE') {
        if (!formData.productName || formData.productName.trim() === '') {
          toast.error('Please enter a product name')
          return
        }
        payload.productName = formData.productName
      }

      if (formData.exemptionType === 'CUSTOMER_TAX_RATE' || formData.exemptionType === 'PRODUCT_TAX_RATE') {
        if (!formData.taxRateId) {
          toast.error('Please select a tax rate')
          return
        }
        payload.taxRateId = formData.taxRateId
      }

      await axios.post('/api/erp/tax-exemptions', payload)
      toast.success('Tax exemption created successfully')
      setShowCreateModal(false)
      resetForm()
      fetchExemptions()
    } catch (error: any) {
      console.error('Error creating tax exemption:', error)
      toast.error(error.response?.data?.error || 'Failed to create tax exemption')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedExemption) return

    try {
      await axios.put(`/api/erp/tax-exemptions/${selectedExemption.id}`, {
        isActive: formData.isActive,
      })
      toast.success('Tax exemption updated successfully')
      setShowEditModal(false)
      resetForm()
      setSelectedExemption(null)
      fetchExemptions()
    } catch (error: any) {
      console.error('Error updating tax exemption:', error)
      toast.error(error.response?.data?.error || 'Failed to update tax exemption')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tax exemption?')) return

    try {
      await axios.delete(`/api/erp/tax-exemptions/${id}`)
      toast.success('Tax exemption deleted successfully')
      fetchExemptions()
    } catch (error: any) {
      console.error('Error deleting tax exemption:', error)
      toast.error(error.response?.data?.error || 'Failed to delete tax exemption')
    }
  }

  const resetForm = () => {
    setFormData({
      exemptionType: 'CUSTOMER',
      customerId: '',
      productName: '',
      taxRateId: '',
      isActive: true,
    })
  }

  const openCreateModal = () => {
    resetForm()
    setShowCreateModal(true)
  }

  const openEditModal = (exemption: TaxExemption) => {
    setSelectedExemption(exemption)
    setFormData({
      exemptionType: exemption.exemptionType,
      customerId: exemption.customerId || '',
      productName: exemption.productName || '',
      taxRateId: exemption.taxRateId || '',
      isActive: exemption.isActive,
    })
    setShowEditModal(true)
  }

  const getExemptionDescription = (exemption: TaxExemption): string => {
    if (exemption.exemptionType === 'CUSTOMER') {
      return `Customer: ${exemption.customer?.name || 'Unknown'} - All Taxes`
    }
    if (exemption.exemptionType === 'CUSTOMER_TAX_RATE') {
      return `Customer: ${exemption.customer?.name || 'Unknown'} - ${exemption.taxRate?.name || 'Unknown Tax'}`
    }
    if (exemption.exemptionType === 'PRODUCT') {
      return `Product: ${exemption.productName} - All Taxes`
    }
    if (exemption.exemptionType === 'PRODUCT_TAX_RATE') {
      return `Product: ${exemption.productName} - ${exemption.taxRate?.name || 'Unknown Tax'}`
    }
    return 'Unknown'
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>
  }

  if (!hasPermission('erp_tax_rate', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You do not have permission to view tax exemptions.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tax Exemptions</h1>
          <p className="text-gray-600 mt-1">Manage tax exemptions for customers and products</p>
        </div>
        {hasPermission('erp_tax_rate', 'write') && (
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Create Tax Exemption
          </button>
        )}
      </div>

      <PageHelp articleTitle="Tax Exemptions" />

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Filter by Type
        </label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="">All Types</option>
          {EXEMPTION_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading tax exemptions...</p>
        </div>
      ) : exemptions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-600">No tax exemptions found. Create your first tax exemption to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {exemptions.map((exemption) => (
                <tr key={exemption.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {EXEMPTION_TYPES.find(t => t.value === exemption.exemptionType)?.label || exemption.exemptionType}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {getExemptionDescription(exemption)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        exemption.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {exemption.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {hasPermission('erp_tax_rate', 'write') && (
                      <>
                        <button
                          onClick={() => openEditModal(exemption)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        {hasPermission('erp_tax_rate', 'delete') && (
                          <button
                            onClick={() => handleDelete(exemption.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Tax Exemption</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exemption Type *
                </label>
                <select
                  value={formData.exemptionType}
                  onChange={(e) => setFormData({ ...formData, exemptionType: e.target.value as TaxExemptionType, customerId: '', productName: '', taxRateId: '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  {EXEMPTION_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              {(formData.exemptionType === 'CUSTOMER' || formData.exemptionType === 'CUSTOMER_TAX_RATE') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer *
                  </label>
                  <select
                    value={formData.customerId}
                    onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {(formData.exemptionType === 'PRODUCT' || formData.exemptionType === 'PRODUCT_TAX_RATE') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={formData.productName}
                    onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter product name or description"
                    required
                  />
                </div>
              )}

              {(formData.exemptionType === 'CUSTOMER_TAX_RATE' || formData.exemptionType === 'PRODUCT_TAX_RATE') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Rate *
                  </label>
                  <select
                    value={formData.taxRateId}
                    onChange={(e) => setFormData({ ...formData, taxRateId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select tax rate</option>
                    {taxRates.map((rate) => (
                      <option key={rate.id} value={rate.id}>
                        {rate.name} ({rate.rate}%)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    resetForm()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedExemption && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Edit Tax Exemption</h2>
            <form onSubmit={handleUpdate}>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Type:</strong> {EXEMPTION_TYPES.find(t => t.value === selectedExemption.exemptionType)?.label || selectedExemption.exemptionType}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Description:</strong> {getExemptionDescription(selectedExemption)}
                </p>
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    resetForm()
                    setSelectedExemption(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default TaxExemptions
